/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { a11y } from './a11y.js';

/*
 * Height transitions, without jQuery.
 *
 * Replaces `jQuery.animate({ height })`, which the tab strip and the point
 * panel used to slide their bodies open and shut. The Web Animations API
 * covers it natively, with two differences worth knowing:
 *
 * - The animation does not change the element's own style, so the final
 *   height is written explicitly. Otherwise the element snaps back the
 *   moment the animation finishes.
 * - `Element.animate` is not implemented everywhere a test might run, and
 *   the callers rely on the completion callback to finish a state change.
 *   Without it the element would be left mid-transition forever, so a
 *   missing implementation jumps straight to the end state.
 */

/**
 * Animate an element's height, then run a callback.
 *
 * Honours the visitor's reduced-motion preference, which `jQuery.animate`
 * could not see: this is motion JavaScript starts, so no stylesheet can
 * reach it.
 *
 * @param {HTMLElement} element - The element to resize.
 * @param {number} toHeight - Target height in pixels.
 * @param {number} duration - Duration in milliseconds.
 * @param {function(): void} [done] - Run once the height has been reached.
 * @returns {Animation|null} The running animation, if there is one.
 */
export function animateHeight(element, toHeight, duration, done) {
    if (!element) {
        done?.();
        return null;
    }

    const target = `${toHeight}px`;
    const from = element.getBoundingClientRect?.().height ?? 0;
    const instant =
        typeof element.animate !== 'function' ||
        a11y.prefersReducedMotion() ||
        !duration;

    if (instant) {
        element.style.height = target;
        done?.();
        return null;
    }

    const animation = element.animate(
        [{ height: `${from}px` }, { height: target }],
        { duration, easing: 'ease-in-out' }
    );
    element.style.height = target;
    animation.addEventListener('finish', () => done?.(), { once: true });
    animation.addEventListener('cancel', () => done?.(), { once: true });
    return animation;
}
